import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useDispatch } from 'store';
import { QuestionAnswerEntry, register } from 'services/auth';
import { FormikHelpers } from 'formik';
import FormSubmitError from 'errors/FormSubmitError';
import { openModal } from 'store/modals';
import useGReCaptcha from 'hooks/useGReCaptcha';
import { PhoneNumberEntry } from 'components/forms/raw/PhoneNumberInput';
import { useSystemSettings } from 'providers/SystemSettingsProvider';
import { getDefaultInitialValue, getQuestionSchema, QuestionFormValues } from 'components/QuestionComponents';
import QuestionTypeRaw from 'types/QuestionTypeRaw';
import { useSearchParams } from 'react-router-dom';

export interface RegisterFormData {
  organizationName: string;
  organizationAddress: string;
  organizationUrl: string;
  country: {
    label: string;
    value: number | string;
  } | null;
  name: string;
  email: string;
  title: string;
  phone: PhoneNumberEntry;
  terms: boolean;
  questions: Record<number, QuestionFormValues>;
}

const getQuestionValue = (question: QuestionFormValues): string => {
  switch (question.type) {
    case QuestionTypeRaw.TEXT:
    case QuestionTypeRaw.URL:
    case QuestionTypeRaw.DISPLAY_ONLY:
      return question.value;
    case QuestionTypeRaw.LONG_TEXT:
      return question.value.value;
    case QuestionTypeRaw.CHECKBOX:
      return Object.keys(question.value)
        .filter((key) => question.value[key])
        .map(window.atob)
        .map((v) => `YES -> ${v}`)
        .join(';');
    case QuestionTypeRaw.SELECTION:
      return question.value?.value ?? '';
    default:
      // @ts-ignore
      throw new Error(`Invalid question type: ${question.type}`);
  }
};

export const getQuestionAnswer = (question: QuestionFormValues): QuestionAnswerEntry => {
  return {
    question_id: question.id!,
    answer: getQuestionValue(question),
  };
};

const useRegisterForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const runCaptchaChallenge = useGReCaptcha();
  const { organization_register_questions } = useSystemSettings();
  const [searchParams] = useSearchParams();

  const schema = useMemo(() => {
    const questions: Record<number, yup.AnySchema> = {};

    for (const question of organization_register_questions) {
      questions[question.id] = yup.object().shape({
        value: getQuestionSchema(question, t),
      });
    }

    return yup.object().shape({
      organizationName: yup.string().required(t('common.form.validation.required')),
      organizationAddress: yup.string().required(t('common.form.validation.required')),
      organizationUrl: yup
        .string()
        .min(1, t('errors.minLength', { count: 1 }))
        .when({
          is: 'N/A',
          then: yup.string().nullable(),
          otherwise: yup
            .string()
            .matches(/^(((http)s?\:)?\/\/)?([a-z0-9][\w\.\-]+\.)+([a-zA-Z0-9]){2,}(\/.*)?$/, t('errors.urlOrNa'))
            .required(t('errors.minLength', { count: 1 })),
        }),
      country: yup.object().nullable().required(t('common.form.validation.required')),
      email: yup.string().email(t('common.form.validation.email')).required(t('common.form.validation.required')),
      name: yup.string().required(t('common.form.validation.required')),
      title: yup.string().required(t('common.form.validation.required')),
      terms: yup.boolean().isTrue(t('common.form.validation.required')).required(t('common.form.validation.required')),
      questions: yup.object().shape(questions),
    });
  }, [t, organization_register_questions]);

  const initialValues = useMemo<RegisterFormData>(() => {
    const questions: Record<number, QuestionFormValues> = {};

    for (const question of organization_register_questions) {
      questions[question.id] = getDefaultInitialValue(question);
    }

    return {
      organizationName: '',
      organizationAddress: '',
      organizationUrl: '',
      country: null,
      name: '',
      email: searchParams.get('email') ?? '',
      title: '',
      phone: {
        prefix: '',
        number: '',
      },
      terms: false,
      questions,
    };
  }, [organization_register_questions, searchParams]);

  const onSubmit = useCallback(
    async (values: RegisterFormData, helpers: FormikHelpers<RegisterFormData>) => {
      try {
        const token = await runCaptchaChallenge();

        // @TODO - Fix actual values for questions and handle it on BE side
        const { data } = await register({
          ...values,
          phone: values.phone.number !== '' ? values.phone.prefix + ' ' + values.phone.number : '',
          country: values.country?.value ?? null,
          token,
          question_answers: Object.keys(values.questions).reduce((acc, key) => {
            acc.push(getQuestionAnswer(values.questions[key as never as keyof typeof values.questions]));

            return acc;
          }, [] as QuestionAnswerEntry[]),
        });

        if (data.error) {
          helpers.setFieldError('terms', data.error_message);
          return;
        }

        dispatch(
          openModal({
            modal: 'registrationSuccessModal',
            data: { ...values },
          }),
        );
      } catch (e) {
        if (FormSubmitError.isInstance(e)) {
          helpers.setFieldError('terms', (e as FormSubmitError).message);
        } else {
          console.error(e);
        }
      }
    },
    [dispatch],
  );

  return {
    schema,
    initialValues,
    onSubmit,
  } as const;
};

export default useRegisterForm;
